// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Anchor,
  Button,
  Checkbox,
  Divider,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { IconBrandApple, IconBrandFacebook, IconBrandGoogle, IconLock, IconMail } from '@tabler/icons-react';
import { useState, useCallback } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { AuthLayout } from '../components/auth/AuthLayout';
import { takeReturnTo } from '../utils/returnTo';
import { MembershipSelector, type ProjectMembership } from '../components/auth/MembershipSelector';
import classes from './SignInPage.module.css';

interface SignInFormValues {
  email: string;
  password: string;
  remember: boolean;
}

export function SignInPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  // State for membership selection flow
  const [showMembershipSelector, setShowMembershipSelector] = useState(false);
  const [pendingMemberships, setPendingMemberships] = useState<ProjectMembership[]>([]);
  const [pendingLogin, setPendingLogin] = useState<string>('');
  const [selectingMembershipId, setSelectingMembershipId] = useState<string | undefined>();
  // Track failed membership selections so user can try other projects
  const [failedMembershipId, setFailedMembershipId] = useState<string | undefined>();
  const [membershipError, setMembershipError] = useState<string | undefined>();

  const form = useForm<SignInFormValues>({
    initialValues: {
      email: '',
      password: '',
      remember: false,
    },
    validate: {
      email: (value) => (!value ? t('errors.emailRequired') : null),
      password: (value) => (!value ? t('errors.passwordRequired') : null),
    },
  });

  /**
   * Select a membership and complete the login process
   */
  const selectMembership = useCallback(
    async (membership: ProjectMembership, login: string): Promise<void> => {
      setSelectingMembershipId(membership.id);
      setLoading(true);
      console.log('[SignIn] Selecting membership:', { id: membership.id, profile: membership.profile.reference });

      try {
        // Note: auth/profile expects the membership ID, not the profile reference
        const profileResponse = await medplum.post('auth/profile', {
          login,
          profile: membership.id,
        });
        console.log('[SignIn] Profile response:', JSON.stringify(profileResponse, null, 2));

        if (profileResponse.code) {
          console.log('[SignIn] Got code from profile, processing...');
          await medplum.processCode(profileResponse.code);
          console.log('[SignIn] Code processed from profile successfully');
          /* Same return-to handling as the direct path, for the same reason. */
          navigate(takeReturnTo() ?? '/');
        }
      } catch (err) {
        console.error('[SignIn] Error selecting membership:', err);
        // Track this as a failed membership so user can try other projects
        // Only show inline error if we're in the membership selector
        if (showMembershipSelector) {
          setFailedMembershipId(membership.id);
          setMembershipError(normalizeErrorString(err));
        } else {
          // Single membership failed during auto-select, show notification
          showNotification({
            color: 'red',
            title: t('auth.signInFailed'),
            message: normalizeErrorString(err),
            autoClose: false,
          });
        }
      } finally {
        setLoading(false);
        setSelectingMembershipId(undefined);
      }
    },
    [medplum, navigate, showMembershipSelector, t]
  );

  const handleSubmit = useCallback(
    async (values: SignInFormValues): Promise<void> => {
      setLoading(true);
      console.log('[SignIn] Submitting login with:', { email: values.email, password: values.password ? '***' : 'EMPTY' });
      try {
        const loginResponse = await medplum.startLogin({
          email: values.email,
          password: values.password,
          /*
           * `offline_access` is what makes the session survive its first hour.
           *
           * The server only mints a refresh secret when the login asks for
           * `offline`/`offline_access` scope (or sets the deprecated `remember`
           * flag) — see includeRefreshToken in the Medplum server. Without it
           * no refresh token is issued, the access token expires after its
           * default one hour, the next request 401s, and onUnauthenticated
           * signs the user out mid-task. That read as "random logouts while in
           * use" because the trigger is time-since-login rather than anything
           * the user did.
           *
           * This does not weaken the session control: the fifteen-minute idle
           * timeout is still what ends an unattended session, and signing out
           * clears the refresh token along with everything else. The refresh
           * token only allows silent renewal while somebody is actually
           * working, which is the behavior that was missing.
           */
          scope: 'openid profile offline_access',
        });
        console.log('[SignIn] Login response:', JSON.stringify(loginResponse, null, 2));

        // If we have a code, process it to complete login
        if (loginResponse.code) {
          console.log('[SignIn] Got code directly, processing...');
          await medplum.processCode(loginResponse.code);
          /*
           * Back to whatever was open when the session ended, rather than the
           * default route. Consumed once, and rejected unless it is an in-app
           * path — see takeReturnTo.
           */
          navigate(takeReturnTo() ?? '/');
        } else if (loginResponse.memberships && loginResponse.memberships.length > 0) {
          console.log('[SignIn] Got memberships:', loginResponse.memberships.length);
          console.log('[SignIn] All memberships:', loginResponse.memberships.map((m: any) => ({
            id: m.id,
            project: m.project?.display,
            projectRef: m.project?.reference,
            profile: m.profile?.display,
          })));

          // Get configured project ID from environment
          const configuredProjectId = import.meta.env.VITE_MEDPLUM_PROJECT_ID;
          console.log('[SignIn] Configured project ID:', configuredProjectId || 'NOT SET');

          // If project ID is configured, auto-select that membership
          if (configuredProjectId) {
            const matchingMembership = loginResponse.memberships.find(
              (m: any) => m.project?.reference === `Project/${configuredProjectId}`
            );
            if (matchingMembership) {
              console.log('[SignIn] Auto-selecting configured project membership:', matchingMembership.project?.reference);
              await selectMembership(matchingMembership as ProjectMembership, loginResponse.login);
              return;
            }
          }

          // Filter out admin-only projects for the selector
          const nonAdminMemberships = loginResponse.memberships.filter((m: any) => {
            const projectName = m.project?.display || '';
            const isAdmin = projectName.toLowerCase().includes('admin');
            console.log(`[SignIn] Project "${projectName}" - isAdmin: ${isAdmin}`);
            return !isAdmin;
          });
          console.log('[SignIn] Non-admin memberships count:', nonAdminMemberships.length);

          // If only one membership total, auto-select it
          if (loginResponse.memberships.length === 1) {
            console.log('[SignIn] Auto-selecting single membership');
            await selectMembership(loginResponse.memberships[0] as ProjectMembership, loginResponse.login);
            return;
          }

          // Multiple memberships - show selector (let user choose)
          // Always show selector so user can pick their project
          console.log('[SignIn] Multiple memberships found, showing selector');
          setPendingMemberships(loginResponse.memberships as ProjectMembership[]
          );
          setPendingLogin(loginResponse.login);
          setShowMembershipSelector(true);
          setLoading(false);
        } else {
          console.log('[SignIn] No code or memberships in response!');
        }
      } catch (err) {
        console.error('[SignIn] Error:', err);
        showNotification({
          color: 'red',
          title: t('auth.signInFailed'),
          message: normalizeErrorString(err),
          autoClose: false,
        });
        setLoading(false);
      }
    },
    [medplum, navigate, selectMembership, t]
  );

  const handleSocialLogin = useCallback((provider: string) => {
    showNotification({
      color: 'blue',
      title: t('auth.comingSoon'),
      message: t('auth.socialLoginNotAvailable', { provider }),
    });
  }, [t]);

  /**
   * Handle membership selection from the selector component
   */
  const handleMembershipSelect = useCallback(
    (membership: ProjectMembership) => {
      selectMembership(membership, pendingLogin);
    },
    [selectMembership, pendingLogin]
  );

  // Show membership selector if multiple memberships are available
  if (showMembershipSelector) {
    return (
      <AuthLayout>
        <MembershipSelector
          memberships={pendingMemberships}
          onSelect={handleMembershipSelect}
          loading={loading}
          selectingId={selectingMembershipId}
          failedMembershipId={failedMembershipId}
          errorMessage={membershipError}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.signIn')} subtitle={t('auth.enterBelowDetails')}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label={t('auth.emailAddress')}
            placeholder={t('auth.enterEmail')}
            leftSection={<IconMail size={16} />}
            size="md"
            {...form.getInputProps('email')}
          />

          <PasswordInput
            label={t('auth.password')}
            placeholder={t('auth.enterPassword')}
            leftSection={<IconLock size={16} />}
            size="md"
            {...form.getInputProps('password')}
          />

          <Group justify="space-between">
            <Checkbox
              label={t('auth.rememberMe')}
              size="sm"
              {...form.getInputProps('remember', { type: 'checkbox' })}
            />
            <Anchor component={Link} to="/forgot-password" size="sm" className={classes.forgotLink}>
              {t('auth.forgotPassword')}
            </Anchor>
          </Group>

          <Button
            type="submit"
            fullWidth
            size="md"
            loading={loading}
            className={classes.submitButton}
          >
            {t('auth.login')}
          </Button>

          <Divider label={t('common.or').toUpperCase()} labelPosition="center" />

          <Group grow gap="sm">
            <ActionIcon
              variant="outline"
              size="xl"
              radius="md"
              className={classes.socialButton}
              aria-label="Sign in with Facebook"
              onClick={() => handleSocialLogin('Facebook')}
            >
              <IconBrandFacebook size={20} color="#1877F2" />
            </ActionIcon>
            <ActionIcon
              variant="outline"
              size="xl"
              radius="md"
              className={classes.socialButton}
              onClick={() => handleSocialLogin('Google')}
              aria-label="Sign in with Google"
            >
              <IconBrandGoogle size={20} color="#DB4437" />
            </ActionIcon>
            <ActionIcon
              variant="outline"
              size="xl"
              radius="md"
              className={classes.socialButton}
              aria-label="Sign in with Apple"
              onClick={() => handleSocialLogin('Apple')}
            >
              <IconBrandApple size={20} />
            </ActionIcon>
          </Group>

          <Text ta="center" size="sm" c="dimmed">
            {t('auth.dontHaveAccount')}{' '}
            <Anchor component={Link} to="/register" className={classes.registerLink}>
              {t('auth.register')}
            </Anchor>
          </Text>
        </Stack>
      </form>
    </AuthLayout>
  );
}
